import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Package, Truck, TrendingUp, Plus, Flame } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [p, pr, s] = await Promise.all([
          api.get("/projects"), api.get("/products"), api.get("/suppliers"),
        ]);
        setProjects(p.data); setProducts(pr.data); setSuppliers(s.data);
      } catch {}
    })();
  }, []);

  const drafts = projects.filter(p => p.status === "draft").length;
  const approved = projects.filter(p => p.status === "approved").length;

  const stats = [
    { label: "Toplam Proje", value: projects.length, icon: FileText, testid: "stat-projects" },
    { label: "Onay Bekleyen", value: drafts, icon: TrendingUp, testid: "stat-drafts" },
    { label: "Ürün Kataloğu", value: products.length, icon: Package, testid: "stat-products" },
    { label: "Tedarikçiler", value: suppliers.length, icon: Truck, testid: "stat-suppliers" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Panel</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight mt-1">Genel Bakış</h2>
        </div>
        <Button data-testid="new-project-btn" onClick={() => nav("/projects/new")} className="bg-primary hover:bg-orange-600 text-white">
          <Plus size={16} className="mr-2" /> Yeni Teklif
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label} data-testid={s.testid} className="p-5 border-border">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{s.label}</div>
                  <div className="font-heading text-3xl font-black mt-2 num">{s.value}</div>
                </div>
                <div className="w-10 h-10 rounded-md bg-orange-50 dark:bg-slate-800 text-primary grid place-items-center">
                  <Icon size={18} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="border-border">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">Son Teklifler</h3>
          <Button variant="ghost" size="sm" onClick={() => nav("/projects")}>Tümünü Gör →</Button>
        </div>
        {projects.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Flame size={32} className="mx-auto mb-3 text-primary" />
            <p className="text-sm">Henüz teklif oluşturulmamış.</p>
            <Button className="mt-4 bg-primary hover:bg-orange-600 text-white" onClick={() => nav("/projects/new")}>
              İlk Teklifi Oluştur
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-left">
                <tr>
                  <th className="px-4 py-2 font-semibold">Teklif No</th>
                  <th className="px-4 py-2 font-semibold">Müşteri</th>
                  <th className="px-4 py-2 font-semibold">İş Türü</th>
                  <th className="px-4 py-2 font-semibold">Durum</th>
                  <th className="px-4 py-2 font-semibold text-right">Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {projects.slice(0, 8).map((p) => (
                  <tr key={p.id} className="border-t border-border hover:bg-slate-50 dark:hover:bg-slate-800">
                    <td className="px-4 py-2 font-mono">{p.quote_no}</td>
                    <td className="px-4 py-2">{p.customer}</td>
                    <td className="px-4 py-2">{p.job_type === 1 ? "Malzeme" : p.job_type === 2 ? "İşçilik" : "Karma"}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs uppercase tracking-widest ${p.status === "approved" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => nav(`/projects/${p.id}`)}>Aç →</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
